import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, HandCoins, Loader2, TriangleAlert, Check, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatCard } from "@/components/Ui";
import { useStations } from "@/components/SearchForm";
import { useAuth, userRoles } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtDateTime, fmtTime, todayStr } from "@/lib/format";

// One boarding station's own sign-out + driver payout. Each station is signed out and paid
// separately, keeps its own station charge (15%) and records its own payout.
function StationPayoutCard({ trip, st, onDone }) {
  const [method, setMethod] = useState("cash");
  const [momo, setMomo] = useState("");
  const [busy, setBusy] = useState(false);
  const smShare = Number(st.sm_share) || 0;
  const [stationAmt, setStationAmt] = useState(String(st.station_fee ?? 0));
  const parsedAmt = parseFloat(stationAmt);
  const stationKeep = Number.isNaN(parsedAmt) ? 0 : Math.min(Math.max(parsedAmt, 0), smShare);
  const driverDue = Math.round((smShare - stationKeep) * 100) / 100;
  const amtInvalid = Number.isNaN(parsedAmt) || parsedAmt < 0 || parsedAmt > smShare + 0.001;
  const pctEff = smShare ? Math.round((stationKeep / smShare) * 1000) / 10 : 0;

  const signout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/station/supplementary-signout", { trip_id: trip.id, station_id: st.station_id });
      const n = data?.nrsa;
      toast.success(`Bus signed out at ${st.boarding_city} · sales now closed`);
      if (n && n.ok === false) toast.warning("NRSA waybill could not be sent — use Retry to re-send it.");
      else if (n && n.ok === true && n.passengers > 0) toast.success(`NRSA waybill sent (${n.passengers} onward)`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const retryWaybill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/station/supplementary-signout/waybill", { trip_id: trip.id, station_id: st.station_id });
      const n = data?.nrsa;
      if (n && n.ok === false) toast.warning("NRSA still unreachable — please try again shortly.");
      else toast.success("NRSA waybill sent");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pay = async () => {
    if (method === "momo" && !(momo || trip.driver_phone)) return toast.error("Enter the driver's MoMo number");
    setBusy(true);
    try {
      const { data } = await api.post("/station/driver-payout", {
        trip_id: trip.id, boarding_station_id: st.station_id,
        driver_id: trip.driver_id || null, driver_name: trip.driver_name || "",
        method, momo_number: method === "momo" ? (momo || trip.driver_phone || "") : "",
        station_amount: stationKeep,
      });
      toast.success(method === "momo"
        ? `${st.boarding_city} driver MoMo payout ${String(data.momo_status || "").includes("simulated") ? "recorded (simulated in preview)" : "sent"}`
        : `${st.boarding_city} driver paid from buffer`);
      setMomo(""); setMethod("cash"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`station-payout-card-${st.station_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[#0052FF]/10 text-[#0052FF]"><MapPin className="h-4 w-4" /></span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Boarding station</div>
            <div className="font-display font-bold text-[#182678]" data-testid={`station-payout-name-${st.station_id}`}>{st.boarding_city}{st.is_origin ? " · origin" : ""}</div>
          </div>
        </div>
        {st.paid && <span className="chip bg-emerald-50 text-emerald-700" data-testid={`station-payout-paid-chip-${st.station_id}`}>Paid</span>}
      </div>

      <div className="space-y-1 rounded-xl bg-blue-50 p-3 text-sm" data-testid={`station-payout-cash-${st.station_id}`}>
        <div className="flex justify-between"><span>Seats booked at {st.boarding_city}</span><b data-testid={`station-payout-seats-${st.station_id}`}>{st.booked}</b></div>
        <div className="flex justify-between"><span>Fare revenue</span><b>{money(st.gross)}</b></div>
        <div className="flex justify-between text-slate-500"><span>− Platform Operator</span><span>{money(st.operator)}</span></div>
        <div className="flex justify-between text-slate-500"><span>− Tracking</span><span>{money(st.tracking)}</span></div>
        <div className="flex justify-between border-t border-blue-200 pt-1 text-[#0052FF]"><span className="font-bold">Station Master share</span><b data-testid={`station-payout-sm-share-${st.station_id}`} className="whitespace-nowrap">{money(st.sm_share)}</b></div>
        <div className="flex justify-between text-slate-500"><span>− Station operation charge ({st.paid ? st.station_charge_pct : pctEff}%)</span><span className="whitespace-nowrap">{money(st.paid ? st.paid_station : stationKeep)}</span></div>
        <div className="flex justify-between border-t border-blue-200 pt-1"><span className="font-bold">Paid to driver</span><b data-testid={`station-payout-driver-due-${st.station_id}`} className="whitespace-nowrap">{money(st.paid ? st.paid_driver : driverDue)}</b></div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{st.boarding_city} keeps{st.paid ? ` (${st.station_charge_pct}%)` : ""}</div>
          {st.paid ? (
            <div className="mt-0.5 whitespace-nowrap font-display text-lg font-extrabold text-slate-800" data-testid={`station-payout-fee-box-${st.station_id}`}>{money(st.paid_station)}</div>
          ) : (
            <>
              <Input type="number" min="0" max={smShare} step="0.01" value={stationAmt} onChange={(e) => setStationAmt(e.target.value)} className="mt-1 h-9 font-display text-base font-extrabold text-slate-800" data-testid={`station-payout-fee-input-${st.station_id}`} />
              <div className="mt-1 text-[10px] text-slate-400">Editable · {pctEff}% of {money(smShare)} share</div>
            </>
          )}
        </div>
        <div className="rounded-xl border border-[#0052FF]/20 bg-[#0052FF]/5 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#0052FF]">Paid to driver</div>
          <div className="mt-0.5 whitespace-nowrap font-display text-lg font-extrabold text-[#0052FF]" data-testid={`station-payout-driver-box-${st.station_id}`}>{money(st.paid ? st.paid_driver : driverDue)}</div>
        </div>
      </div>
      {!st.paid && amtInvalid && <div className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600" data-testid={`station-payout-amt-error-${st.station_id}`}>Station amount must be between {money(0)} and {money(smShare)}.</div>}

      {st.signed_out && !st.is_origin && st.nrsa && st.nrsa.ok === false && (
        <div className="flex items-center justify-between gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid={`station-nrsa-warning-${st.station_id}`}>
          <span className="flex items-center gap-2"><TriangleAlert className="h-4 w-4" />NRSA waybill not sent</span>
          <Button type="button" size="sm" variant="outline" onClick={retryWaybill} disabled={busy} className="rounded-full border-amber-300 bg-white text-amber-800 hover:bg-amber-100" data-testid={`station-nrsa-retry-${st.station_id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Retry"}</Button>
        </div>
      )}
      {st.signed_out && !st.is_origin && st.nrsa && st.nrsa.ok === true && st.nrsa.passengers > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700" data-testid={`station-nrsa-ok-${st.station_id}`}><Check className="h-4 w-4" />NRSA waybill sent · {st.nrsa.passengers} onward</div>
      )}

      {st.paid ? (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-700" data-testid={`station-payout-already-paid-${st.station_id}`}>
          Driver paid {money(st.paid_driver)} via {st.paid_method === "momo" ? "MoMo" : "cash"}{st.paid_method === "momo" && st.momo_status ? ` (${String(st.momo_status).includes("simulated") ? "simulated" : String(st.momo_status).includes("submitted") ? "sent" : st.momo_status})` : ""} · {st.boarding_city} kept {money(st.paid_station)}
        </div>
      ) : !st.signed_out ? (
        st.is_origin ? (
          <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid={`station-payout-needs-dispatch-${st.station_id}`}>Sign out (dispatch) this bus from Trip management before paying the driver.</div>
        ) : (
          <Button type="button" onClick={signout} disabled={busy} className="h-auto w-full whitespace-normal rounded-xl bg-slate-800 py-2.5 font-bold leading-tight hover:bg-slate-900" data-testid={`station-signout-button-${st.station_id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Sign out bus at ${st.boarding_city}`}</Button>
        )
      ) : st.driver_due <= 0 ? (
        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500" data-testid={`station-payout-nothing-${st.station_id}`}>No boarding revenue at {st.boarding_city} for this bus.</div>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-slate-600">Pay by</Label>
          <Select value={method} onValueChange={setMethod} disabled>
            <SelectTrigger data-testid={`station-payout-method-${st.station_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash (from {st.boarding_city} buffer)</SelectItem>
            </SelectContent>
          </Select>
          {method === "momo" && (
            <>
              <Label className="text-xs font-semibold text-slate-600">Driver MoMo number</Label>
              <Input value={momo} onChange={(e) => setMomo(e.target.value.replace(/[^\d+]/g, ""))} placeholder={trip.driver_phone || "024 123 4567"} inputMode="tel" data-testid={`station-payout-momo-${st.station_id}`} />
            </>
          )}
          <Button type="button" onClick={pay} disabled={busy || amtInvalid} className="w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid={`station-payout-pay-button-${st.station_id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay driver ${money(driverDue)} ${method === "momo" ? "by MoMo" : "cash"}`}</Button>
        </div>
      )}
    </div>
  );
}

function PayoutForm({ stationId, onDone }) {
  const [date, setDate] = useState(todayStr());
  const [trips, setTrips] = useState([]);
  const [reload, setReload] = useState(0);
  const [tripId, setTripId] = useState("");
  useEffect(() => {
    let ignore = false;
    setTripId("");
    api.get("/station/trips", { params: { date, ...(stationId ? { station_id: stationId } : {}) } })
      .then((r) => { if (!ignore) setTrips(r.data); })
      .catch(() => { if (!ignore) setTrips([]); });
    return () => { ignore = true; };
  }, [date, reload, stationId]);
  const sel = trips.find((t) => t.id === tripId);
  const done = () => { setReload((n) => n + 1); onDone(); };

  return (
    <form onSubmit={(e) => e.preventDefault()} className="card-soft space-y-4 p-5" data-testid="driver-payout-form">
      <div>
        <div className="font-display font-bold">Sign out bus &amp; pay driver</div>
        <p className="text-xs text-slate-500">Each boarding station signs out and pays the driver for its own passengers — every station keeps its own charge and is recorded separately.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Trip date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payout-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Trip / manifest</Label>
          <Select value={tripId} onValueChange={setTripId}>
            <SelectTrigger data-testid="payout-trip-select"><SelectValue placeholder="Select a departure" /></SelectTrigger>
            <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}{t.stations.length > 1 ? ` · boards ${t.stations.map((s) => s.boarding_city).join(", ")}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {sel && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="payout-station-cards">
          {sel.stations.map((st) => <StationPayoutCard key={st.station_id} trip={sel} st={st} onDone={done} />)}
        </div>
      )}
    </form>
  );
}

export default function StationCash() {
  const { user } = useAuth();
  const isSM = userRoles(user).includes("station_master") && !userRoles(user).some((r) => ["admin", "manager"].includes(r)) && !!user?.station_id;
  const stations = useStations();
  const [sid, setSid] = useState("");
  const effSid = isSM ? (user?.station_id || "") : sid;
  const stationParams = effSid ? { station_id: effSid } : null;
  const [buf, setBuf] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [topups, setTopups] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [histDate, setHistDate] = useState(todayStr());

  const loadPayouts = useCallback(async () => {
    try { const { data } = await api.get("/station/driver-payouts", { params: effSid ? { station_id: effSid } : {} }); setPayouts(data); } catch (e) { toast.error(errMsg(e)); }
  }, [effSid]);

  const loadBuffer = useCallback(async () => {
    if (!effSid) { setBuf(null); setTopups([]); setIncoming([]); return; }
    setBuf(null);
    try {
      const [b, t, inc] = await Promise.all([
        api.get("/station/buffer", { params: { station_id: effSid } }),
        api.get("/station/buffer-topups", { params: { station_id: effSid } }),
        api.get("/station/cash-transfers/incoming", { params: { station_id: effSid } }),
      ]);
      setBuf(b.data); setTopups(t.data); setIncoming(inc.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [effSid]);

  const reloadAll = useCallback(() => { loadBuffer(); loadPayouts(); }, [loadBuffer, loadPayouts]);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);
  useEffect(() => { loadBuffer(); }, [loadBuffer]);

  const ack = async (id) => {
    try { await api.post(`/station/buffer-topups/${id}/acknowledge`); toast.success("Buffer receipt acknowledged"); loadBuffer(); } catch (e) { toast.error(errMsg(e)); }
  };
  const actTransfer = async (id, accept) => {
    try {
      if (accept) { const { data } = await api.post(`/cash-transfers/${id}/accept`); toast.success(`Accepted ${money(data.amount)} into the station buffer`); }
      else { const reason = window.prompt("Why are you rejecting this cash?"); if (!reason) return; await api.post(`/cash-transfers/${id}/reject`, { reason }); toast.success("Rejected"); }
      loadBuffer();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const shownPayouts = histDate ? payouts.filter((p) => (p.created_at || "").slice(0, 10) === histDate) : payouts;
  const shownTotal = shownPayouts.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="station-cash">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Pay drivers &amp; station cash</h2>
        <p className="text-sm text-slate-500">Sign out each boarding station and pay the driver separately — every station keeps its own 15% and is recorded on its own.</p>
      </div>

      <div className="card-soft flex flex-col gap-1.5 p-4 sm:max-w-md" data-testid="station-cash-top-filter">
        <Label className="text-xs font-semibold text-slate-600">Station</Label>
        {isSM ? (
          <Select value={effSid} disabled>
            <SelectTrigger data-testid="station-cash-station-select"><SelectValue placeholder="Your station" /></SelectTrigger>
            <SelectContent>{stations.filter((s) => s.id === effSid).map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger data-testid="station-cash-station-select"><SelectValue placeholder="Select a station" /></SelectTrigger>
            <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <p className="text-xs text-slate-500">Driver payouts, trips and the cash buffer below all follow this station.{isSM ? " You are locked to your assigned station." : ""}</p>
      </div>

      {!effSid ? (
        <p className="card-soft p-6 text-center text-sm text-slate-500" data-testid="station-cash-pick-station">Select a station above to see its driver payouts and cash buffer.</p>
      ) : (
        <>
          <PayoutForm stationId={effSid} onDone={reloadAll} />

          <div className="space-y-4">
            <h3 className="font-display text-lg font-extrabold tracking-tight">Station cash buffer</h3>
            {!buf ? <div className="grid h-32 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
              <>
                {buf.low && <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-sm font-semibold text-rose-700" data-testid="buffer-low-alert"><TriangleAlert className="h-4 w-4" />Buffer running low (under 5% of the limit) — ask the cashier to top up.</div>}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  <StatCard label="Buffer limit" value={money(buf.buffer_amount)} icon={Wallet} testid="buffer-limit" />
                  <StatCard label="Opening balance" value={money(buf.opening_balance)} icon={Wallet} tint="bg-slate-100 text-slate-600" testid="buffer-opening" />
                  <StatCard label="Buffer received today" value={money(buf.today_received)} icon={HandCoins} tint="bg-sky-50 text-sky-600" testid="buffer-today-received" />
                  <StatCard label="Given to buses today" value={money(buf.today_given)} icon={HandCoins} tint="bg-amber-50 text-amber-600" testid="buffer-today-given" />
                  <StatCard label="Balance" value={money(buf.balance)} icon={Wallet} tint={buf.low ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"} testid="buffer-balance" />
                </div>
                {topups.filter((t) => !t.sm_ack).length > 0 && (
                  <div className="card-soft space-y-2 border-violet-200 bg-violet-50/60 p-4" data-testid="buffer-incoming-topups">
                    <div className="label-xs text-violet-700">Buffer sent by cashier — acknowledge receipt</div>
                    {topups.filter((t) => !t.sm_ack).map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm" data-testid={`topup-pending-${t.id}`}>
                        <span><b>{money(t.amount)}</b> from {t.cashier_name} · {fmtDateTime(t.created_at)}</span>
                        <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" onClick={() => ack(t.id)} data-testid={`ack-topup-${t.id}`}><Check className="h-4 w-4" />Acknowledge</Button>
                      </div>
                    ))}
                  </div>
                )}
                {incoming.length > 0 && (
                  <div className="card-soft space-y-2 border-amber-200 bg-amber-50/60 p-4" data-testid="station-incoming-transfers">
                    <div className="label-xs text-amber-700">Cash handed in by a teller — confirm to add it to the buffer</div>
                    {incoming.map((c) => (
                      <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`station-transfer-${c.id}`}>
                        <span><b>{money(c.amount)}</b> from {c.from_teller_name}{c.trip_label ? ` · ${c.trip_label}` : ""} · {fmtDateTime(c.created_at)}</span>
                        <div className="flex gap-2">
                          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => actTransfer(c.id, true)} data-testid={`accept-station-transfer-${c.id}`}><Check className="h-4 w-4" />Accept</Button>
                          <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-600" onClick={() => actTransfer(c.id, false)} data-testid={`reject-station-transfer-${c.id}`}><X className="h-4 w-4" />Reject</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="card-soft divide-y divide-slate-100">
              <div className="flex flex-wrap items-center justify-between gap-2 p-4">
                <div className="label-xs">Driver payouts{histDate ? "" : " · all dates"}{shownPayouts.length ? ` · ${money(shownTotal)}` : ""}</div>
                <div className="flex items-center gap-2">
                  <Input type="date" value={histDate} onChange={(e) => setHistDate(e.target.value)} className="h-9 w-40" data-testid="payout-history-date" />
                  <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setHistDate(histDate ? "" : todayStr())} data-testid="payout-history-toggle">{histDate ? "Show all" : "Today"}</Button>
                </div>
              </div>
              {shownPayouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-4 text-sm" data-testid={`payout-row-${p.id}`}>
                  <div>
                    <b>{p.driver_name || "Driver"}</b> {p.bus_number ? <span className="text-slate-500">· Bus {p.bus_number}</span> : null}
                    {p.station_name ? <span className="chip ml-1 bg-blue-50 text-[#0052FF]">{p.station_name}</span> : null}
                    {p.method === "momo" ? <span className="chip ml-1 bg-violet-50 text-violet-700">MoMo</span> : <span className="chip ml-1 bg-emerald-50 text-emerald-700">Cash</span>}
                    {p.route ? <div className="text-xs text-slate-500">{p.route}{p.departure ? ` · ${fmtTime(p.departure)}` : ""}</div> : null}
                    <div className="text-xs text-slate-500">{fmtDateTime(p.created_at)} · {p.by_name}{p.station_amount ? ` · station kept ${money(p.station_amount)}` : ""}</div>
                  </div>
                  <b className="text-rose-600">- {money(p.amount)}</b>
                </div>
              ))}
              {!shownPayouts.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="payouts-empty">{histDate ? "No driver payouts on this date." : "No driver payouts yet."}</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
